import { useState } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Button } from '@shared/ui/primitives/Button';
import { Banner } from '@shared/ui/feedback/Banner';
import { navigateTo } from '@shared/lib/navigation';
import { switchTenant } from '@features/auth/api';
import { useTenantSwitchPrompt } from './tenantSwitchPromptStore';

/**
 * Confirms before switching the user into another linked community to open a
 * notification's target. Mounted once at the app root (outside the router), so
 * both push taps and the in-app notifications list can trigger it. After a
 * successful switch we navigate to the stashed path — a full load is fine and
 * desirable here, since the switch resets all tenant-scoped caches anyway.
 */
export function TenantSwitchPrompt(): JSX.Element | null {
  const pending = useTenantSwitchPrompt((s) => s.pending);
  const clear = useTenantSwitchPrompt((s) => s.clear);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onConfirm = async (): Promise<void> => {
    if (!pending) return;
    setSwitching(true);
    setError(null);
    try {
      await switchTenant(pending.tenantId);
      const path = pending.path;
      clear();
      navigateTo(path);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch community. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <BottomSheet
      open={pending != null}
      onOpenChange={(open) => {
        if (!open && !switching) {
          clear();
          setError(null);
        }
      }}
      title="Switch community?"
      description={pending ? `This notification is for ${pending.tenantName}.` : undefined}
      height="auto"
    >
      <div className="space-y-5 pb-4">
        {error ? <Banner variant="danger">{error}</Banner> : null}
        <p className="text-body leading-7 text-ink-secondary">
          You're currently in a different community. Switch to{' '}
          <span className="font-semibold text-ink">{pending?.tenantName}</span> to view this?
        </p>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="ghost"
            size="md"
            className="flex-1"
            disabled={switching}
            onClick={() => {
              clear();
              setError(null);
            }}
          >
            Not now
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            className="flex-1"
            disabled={switching}
            onClick={() => void onConfirm()}
          >
            <ArrowRightLeft className="h-4 w-4" strokeWidth={1.75} />
            {switching ? 'Switching…' : 'Switch & view'}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}
